import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Card, DatePicker, Empty, Image, Input, List, message as antdMessage, Modal, Pagination, Popconfirm, Space, Spin, Tabs, Typography } from 'antd';
import { LikeOutlined, LikeFilled, UserOutlined } from '@ant-design/icons';
import { apiClient } from '../contexts/AuthContext';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

// 社群互动页面
const Community = () => {
  const [tabs, setTabs] = useState([]); // { key, label, raw }
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [tabsError, setTabsError] = useState(null);

  const [messages, setMessages] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [likingIds, setLikingIds] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({}); // seq -> content
  const [commentLoadingIds, setCommentLoadingIds] = useState({});

  const localUser = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('parse user from localStorage failed', e);
      return null;
    }
  }, []);

  const customerId = localUser?.customerId;
  // userId 字段名根据现有 user 结构做一次兜底
  const userId = localUser?.userId || localUser?.id || localUser?.loginId || localUser?.username;

  // Helper function to check if liked by me
  const isLikedByMe = (thumbsUpUsers, localUser) => {
    if (!Array.isArray(thumbsUpUsers) || !localUser) return false;
    const { customerId, userId, name } = localUser;
    return thumbsUpUsers.some(userStr => {
      const parts = userStr.split('_');
      if (parts.length < 3) return false;
      const [cId, uId, n] = parts;
      return cId === customerId && uId === userId && n === name;
    });
  };

  // Helper function to check if comment is by me
  const isCommentByMe = (comment, localUser) => {
    if (!comment || !localUser) return false;
    const { customerId, userId, name } = localUser;
    return comment.customerId === customerId && comment.userId === userId && comment.nickName === name;
  };

  // Helper function to decode URL encoded string
  const decodeUrlEncoded = (str) => {
    if (!str || typeof str !== 'string') return str;
    try {
      // 检查是否是 URL 编码的字符串（包含 %XX 格式）
      if (/%[0-9A-Fa-f]{2}/.test(str)) {
        return decodeURIComponent(str);
      }
      return str;
    } catch (e) {
      console.error('decode URL encoded string failed', e);
      return str;
    }
  };

  // 加载栏目(tab) 配置
  useEffect(() => {
    const fetchTabs = async () => {
      setTabsLoading(true);
      setTabsError(null);
      try {
        const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
          tableName: 'INTERACTIVE_MESSAGE',
          colName: 'INTERACTIVE_MESSAGE'
        });

        const raw = res?.data?.data;
        let parsedTabs = [];

        if (Array.isArray(raw)) {
          // 有些实现可能已经是数组
          parsedTabs = raw;
        } else if (typeof raw === 'string') {
          // 题目返回 data 是字符串形式的 JSON 数组
          try {
            parsedTabs = JSON.parse(raw);
          } catch (e) {
            console.error('parse enum data error', e);
          }
        }

        const finalTabs = (parsedTabs || [])
          .filter(item => item && item.enumCode && item.enumName)
          .sort((a, b) => (a.showSort || 0) - (b.showSort || 0))
          .map(item => ({
            key: item.enumCode,
            label: item.enumName,
            raw: item
          }));

        setTabs(finalTabs);
        if (!activeTabKey && finalTabs.length > 0) {
          setActiveTabKey(finalTabs[0].key);
        }
      } catch (err) {
        console.error('fetch enum tabs failed', err);
        setTabsError('获取栏目配置失败');
        antdMessage.error('获取栏目配置失败');
      } finally {
        setTabsLoading(false);
      }
    };

    fetchTabs();
  }, [activeTabKey]);

  // 加载当前 tab 的消息列表
  useEffect(() => {
    const fetchMessages = async () => {
      if (!activeTabKey || !customerId) return;

      setListLoading(true);
      setListError(null);
      try {
        const body = {
          customerId,
          tag: activeTabKey,
          pageSize,
          pageNo
        };

        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
          body.startDate = dateRange[0].format('YYYY-MM-DD');
          body.endDate = dateRange[1].format('YYYY-MM-DD');
        }

        const res = await apiClient.post('/sports-server-prod/taller.api.v1/message/pageList', body);
        const data = res?.data?.data || {};
        const list = data.list || data.records || [];
        const totalRecord = data.totalRecord || data.total || list.length;

        // 给每条记录加上前端使用的点赞状态字段
        const withLikeState = list.map(item => ({
          ...item,
          likeCount: item.thumbsUpCount || 0,
          likedByMe: isLikedByMe(item.thumbsUpUsers, localUser)
        }));

        setMessages(withLikeState);
        setTotal(totalRecord);
      } catch (err) {
        console.error('fetch messages failed', err);
        setListError('获取消息列表失败');
        antdMessage.error('获取消息列表失败');
      } finally {
        setListLoading(false);
      }
    };

    fetchMessages();
  }, [activeTabKey, pageNo, pageSize, dateRange, customerId, refreshTrigger]);

  const handleTabChange = (key) => {
    setActiveTabKey(key);
    setPageNo(1);
  };

  const handlePageChange = (page, size) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleLike = async (msg) => {
    if (!customerId || !userId) {
      antdMessage.warning('未获取到当前登录用户信息');
      return;
    }
    if (!msg?.seq) {
      return;
    }

    if (likingIds[msg.seq]) return; // 防止重复点击

    setLikingIds(prev => ({ ...prev, [msg.seq]: true }));

    // 乐观更新
    setMessages(prev =>
      prev.map(item => {
        if (item.seq !== msg.seq) return item;
        const liked = !item.likedByMe;
        const delta = liked ? 1 : -1;
        return {
          ...item,
          likedByMe: liked,
          likeCount: Math.max(0, (item.likeCount || 0) + delta)
        };
      })
    );

    try {
      await apiClient.post('/sports-server-prod/taller.api.v1/comments/thumbsup', {
        parentId: msg.seq,
        customerId,
        userId,
        commentType: 'Z',
        nickName: localUser?.name,
        userAvatar: localUser?.avatar
      });
    } catch (err) {
      console.error('thumbsup failed', err);
      antdMessage.error('点赞失败');
      // 失败时回滚
      setMessages(prev =>
        prev.map(item => {
          if (item.seq !== msg.seq) return item;
          const liked = !item.likedByMe;
          const delta = liked ? 1 : -1;
          return {
            ...item,
            likedByMe: liked,
            likeCount: Math.max(0, (item.likeCount || 0) + delta)
          };
        })
      );
    } finally {
      setLikingIds(prev => {
        const clone = { ...prev };
        delete clone[msg.seq];
        return clone;
      });
    }
  };

  const handleCommentChange = (seq, value) => {
    setCommentDrafts(prev => ({ ...prev, [seq]: value }));
  };

  const handleSubmitComment = async (msg) => {
    if (!customerId || !userId) {
      antdMessage.warning('未获取到当前登录用户信息');
      return;
    }
    if (!msg?.seq) return;

    const content = (commentDrafts[msg.seq] || '').trim();
    if (!content) {
      antdMessage.warning('请输入评论内容');
      return;
    }

    setCommentLoadingIds(prev => ({ ...prev, [msg.seq]: true }));

    try {
      await apiClient.post('/sports-server-prod/taller.api.v1/comments/addComment', {
        parentId: msg.seq,
        customerId,
        userId,
        commentType: 'C',
        content,
        nickName: localUser?.name,
        userAvatar: localUser?.avatar
      });
      antdMessage.success('评论成功');
      setCommentDrafts(prev => ({ ...prev, [msg.seq]: '' }));
      // 如需展示评论数量，可在这里选择性刷新当前页
    } catch (err) {
      console.error('addComment failed', err);
      antdMessage.error('评论失败');
    } finally {
      setCommentLoadingIds(prev => {
        const clone = { ...prev };
        delete clone[msg.seq];
        return clone;
      });
    }
  };

  const handleDeleteComment = async (commentId, msgSeq) => {
    try {
      await apiClient.post(`/sports-server-prod/taller.api.v1/comments/delete?id=${String(commentId)}`);
      antdMessage.success('删除成功');
      // 更新 messages，移除该评论
      setMessages(prev => prev.map(item => {
        if (item.seq !== msgSeq) return item;
        return {
          ...item,
          comments: item.comments.filter(c => c.id !== commentId)
        };
      }));
    } catch (err) {
      console.error('delete comment failed', err);
      antdMessage.error('删除失败');
    }
  };

  const renderMedia = (msg) => {
    const { mediaType, mediaUrl, picUrlArr, picUrl } = msg;

    if (mediaType === 'video' && mediaUrl) {
      return (
        <video
          src={mediaUrl}
          controls
          style={{ width: '100%', maxHeight: 360, borderRadius: 8, background: '#000', marginTop: 8 }}
        />
      );
    }

    let images = [];
    if (Array.isArray(picUrlArr)) {
      images = picUrlArr;
    } else if (typeof picUrlArr === 'string') {
      try {
        const parsed = JSON.parse(picUrlArr);
        if (Array.isArray(parsed)) images = parsed;
      } catch (e) {
        // ignore
      }
    }
    if (!images.length && picUrl) {
      images = [picUrl];
    }

    // 过滤掉无效的图片URL
    const validImages = images.filter(url => 
      url && 
      typeof url === 'string' && 
      url.trim() !== '' && 
      (url.startsWith('http') || url.startsWith('https') || url.startsWith('/'))
    );

    if (!validImages.length) return null;

    return (
      <Image.PreviewGroup>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {validImages.map((url, idx) => (
            <Image
              key={idx}
              src={url}
              alt="pic"
              width={120}
              height={120}
              style={{ objectFit: 'cover', borderRadius: 4 }}
              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=="
            />
          ))}
        </div>
      </Image.PreviewGroup>
    );
  };

  const getImages = (msg) => {
    const { picUrlArr, picUrl } = msg;
    let images = [];
    if (Array.isArray(picUrlArr)) {
      images = picUrlArr;
    } else if (typeof picUrlArr === 'string') {
      try {
        const parsed = JSON.parse(picUrlArr);
        if (Array.isArray(parsed)) images = parsed;
      } catch (e) {
        // ignore
      }
    }
    if (!images.length && picUrl) {
      images = [picUrl];
    }
    return images;
  };

  const handleAINutritionist = async (msg) => {
    const images = getImages(msg);
    if (!images.length) {
      antdMessage.warning('该消息没有图片，无法分析');
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const res = await apiClient.post('/sports-server-prod/taller.api.v1/aidietitian/analyzeImages', images, {
        headers: {
          accessToken: accessToken
        }
      });
      const data = res?.data?.data;
      if (!data || !data.trim()) {
        antdMessage.warning('AI营养师当前无法解析该图片，请稍后再试');
        return;
      }
      // 自动评论
      await apiClient.post('/sports-server-prod/taller.api.v1/comments/addComment', {
        parentId: msg.seq,
        customerId: msg.customerId,
        userId: '000000',
        commentType: 'C',
        content: data,
        nickName: 'AI营养师',
        userAvatar: 'https://wecarebaby-prod.oss-cn-hangzhou.aliyuncs.com/weCareTaller/attachment/S00001/20260103211844403/c3c60218d9bb4f2eb9494c9bbb996ec3.png'
      });
      antdMessage.success('AI营养师分析完成');
      // 刷新评论区
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('AI analyze failed', err);
      antdMessage.error('AI分析失败');
    }
  };

  const renderItem = (item) => {
    const seq = item.seq;
    const commentValue = commentDrafts[seq] || '';
    const isCommentLoading = !!commentLoadingIds[seq];
    const isLiking = !!likingIds[seq];
    const currentTab = tabs.find(t => t.key === activeTabKey);
    const currentTabLabel = currentTab?.label;

    return (
      <Card
        key={seq}
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Avatar
            size={40}
            src={item.headerImgUrl}
            icon={<UserOutlined />}
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>{decodeUrlEncoded(item.myName) || '匿名用户'}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.registDate || ''}
              </Text>
            </div>
            <div style={{ marginTop: 8, marginBottom: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {item.context || ''}
            </div>
            {renderMedia(item)}
            <div style={{ marginTop: 12 }}>
              <Space size={16}>
                <Button
                  type={item.likedByMe ? 'primary' : 'default'}
                  icon={item.likedByMe ? <LikeFilled /> : <LikeOutlined />}
                  size="small"
                  loading={isLiking}
                  onClick={() => handleLike(item)}
                >
                  点赞{typeof item.likeCount === 'number' ? `(${item.likeCount})` : ''}
                </Button>
                {currentTabLabel === '饮食打卡' && getImages(item).length > 0 && (
                  <Popconfirm
                    title="确认调用AI营养师"
                    description="确定要让AI营养师分析吗？"
                    onConfirm={() => handleAINutritionist(item)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="primary" size="small">
                      AI营养师
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>
            <div style={{ marginTop: 12 }}>
              <Input.TextArea
                rows={2}
                placeholder="请输入评论内容"
                value={commentValue}
                maxLength={200}
                onChange={(e) => handleCommentChange(seq, e.target.value)}
              />
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  loading={isCommentLoading}
                  onClick={() => handleSubmitComment(item)}
                >
                  发表评论
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          {Array.isArray(item.comments) && item.comments.length > 0 && (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                {`评论 (${item.comments.length})`}
              </div>
              {item.comments.map(comment => {
                const isMyComment = true;
                return (
                  <div key={comment.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Avatar
                      size={32}
                      src={comment.userAvatar}
                      icon={<UserOutlined />}
                    />
                    <div style={{ flex: 1, marginLeft: 8, padding: 8, borderRadius: 8, background: '#f5f5f5' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{decodeUrlEncoded(comment.nickName) || '匿名用户'}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {comment.createDate || ''}
                        </Text>
                      </div>
                      <div style={{ marginTop: 4, marginBottom: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {comment.content}
                      </div>
                      {isMyComment && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Popconfirm
                            title="确认删除"
                            description="确定要删除这条评论吗？"
                            onConfirm={() => handleDeleteComment(comment.id, seq)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button type="link" size="small">删除</Button>
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    );
  };

  const tabItems = tabs.map(t => ({ key: t.key, label: t.label }));

  return (
    <div style={{ padding: 16 }}>
      <Card bodyStyle={{ padding: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              社群互动
            </Typography.Title>
          </div>

          <div>
            {tabsLoading ? (
              <Spin />
            ) : (
              <Tabs
                items={tabItems}
                activeKey={activeTabKey || (tabItems[0] && tabItems[0].key)}
                onChange={handleTabChange}
                tabBarGutter={16}
              />
            )}
            {tabsError && (
              <Text type="danger" style={{ marginTop: 8 }}>
                {tabsError}
              </Text>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Text>时间范围：</Text>
              <RangePicker
                value={dateRange}
                onChange={(val) => {
                  setPageNo(1);
                  setDateRange(val);
                }}
                allowClear
              />
              <Button onClick={() => setRefreshTrigger(prev => prev + 1)}>刷新</Button>
            </Space>
          </div>

          <div>
            <Spin spinning={listLoading}>
              {listError && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="danger">{listError}</Text>
                </div>
              )}
              {(!messages || messages.length === 0) && !listLoading ? (
                <Empty description="暂无数据" />
              ) : (
                <List
                  dataSource={messages}
                  renderItem={renderItem}
                />
              )}
            </Spin>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              current={pageNo}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showTotal={(t) => `共 ${t} 条`}
            />
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Community;

